import AdminPanelSettingsOutlined from "@mui/icons-material/AdminPanelSettingsOutlined";
import Google from "@mui/icons-material/Google";
import Alert from "@mui/material/Alert";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { type FormEvent, useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router-dom";
import { z } from "zod";
import { getAuthErrorMessage } from "@bw/shared";
import { useAuth } from "@bw/shared/hooks";
import { validateReturnUrl } from "@bw/shared/utils";
import { hasPlatformConsoleAccess } from "../platformAccess";

const loginFormSchema = z.object({
  email: z.string().min(1, "Email is required").email("Invalid email address"),
  password: z.string().min(1, "Password is required"),
});

type LoginFormValues = z.infer<typeof loginFormSchema>;

export function LoginPage() {
  const { authError, signIn, signInWithEmail, user, loading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const hasPlatformAccess = hasPlatformConsoleAccess(user);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const submissionLock = useRef(false);
  const form = useForm<LoginFormValues>({
    defaultValues: { email: "", password: "" },
  });
  const { ref: emailRef, ...emailField } = form.register("email");
  const { ref: passwordRef, ...passwordField } = form.register("password");

  useEffect(() => {
    if (user && !loading) {
      if (hasPlatformAccess) {
        const returnUrl = validateReturnUrl(searchParams.get("returnUrl"));
        navigate(returnUrl, { replace: true });
      } else {
        navigate("/access-denied", { replace: true });
      }
    }
  }, [user, loading, hasPlatformAccess, navigate, searchParams]);

  const runSignIn = async (action: () => Promise<void>) => {
    if (submissionLock.current) return;
    submissionLock.current = true;
    setError(null);
    setSubmitting(true);
    try {
      await action();
    } catch (signInError) {
      setError(getAuthErrorMessage(signInError));
    } finally {
      submissionLock.current = false;
      setSubmitting(false);
    }
  };

  const submitEmail = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (submissionLock.current) return;
    form.clearErrors();
    const result = loginFormSchema.safeParse(form.getValues());
    if (!result.success) {
      for (const field of ["email", "password"] as const) {
        const issue = result.error.issues.find(({ path }) => path[0] === field);
        if (issue) {
          form.setError(field, { type: "validate", message: issue.message });
        }
      }
      return;
    }
    void runSignIn(() => {
      if (!signInWithEmail) throw new Error("Email sign-in is unavailable");
      return signInWithEmail(result.data.email, result.data.password);
    });
  };

  if (loading) {
    return (
      <Box
        role="status"
        aria-label="Loading"
        sx={{
          display: "flex",
          minHeight: "100dvh",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "background.default",
        }}
      >
        <CircularProgress size={36} />
      </Box>
    );
  }

  return (
    <Box
      component="main"
      sx={{
        display: "flex",
        minHeight: "100dvh",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "background.default",
        px: 2,
        py: 6,
      }}
    >
      <Paper
        component="form"
        noValidate
        onSubmit={submitEmail}
        variant="outlined"
        sx={{ width: "100%", maxWidth: 400, p: { xs: 3, sm: 4 } }}
      >
        <Stack spacing={3} sx={{ alignItems: "center" }}>
          <Avatar
            sx={{
              width: 52,
              height: 52,
              bgcolor: "primary.main",
              color: "primary.contrastText",
            }}
          >
            <AdminPanelSettingsOutlined />
          </Avatar>
          <Box sx={{ textAlign: "center" }}>
            <Typography component="h1" variant="h5" sx={{ fontWeight: 700 }}>
              BW Platform
            </Typography>
            <Typography color="text.secondary" variant="body2" sx={{ mt: 1 }}>
              Sign in with your approved internal account.
            </Typography>
          </Box>
          {error ?? authError ? (
            <Alert role="alert" severity="error" sx={{ width: "100%" }}>
              {error ?? authError}
            </Alert>
          ) : null}
          <Stack spacing={2} sx={{ width: "100%" }}>
            <TextField
              {...emailField}
              inputRef={emailRef}
              label="Email"
              type="email"
              autoComplete="username"
              required
              fullWidth
              size="small"
              disabled={submitting}
              error={Boolean(form.formState.errors.email)}
              helperText={form.formState.errors.email?.message}
            />
            <TextField
              {...passwordField}
              inputRef={passwordRef}
              label="Password"
              type="password"
              autoComplete="current-password"
              required
              fullWidth
              size="small"
              disabled={submitting}
              error={Boolean(form.formState.errors.password)}
              helperText={form.formState.errors.password?.message}
            />
            <Button
              fullWidth
              variant="contained"
              size="small"
              type="submit"
              disabled={submitting}
            >
              {submitting ? "Signing in…" : "Sign in"}
            </Button>
            <Divider>or</Divider>
            <Button
              fullWidth
              variant="outlined"
              size="small"
              type="button"
              startIcon={<Google />}
              onClick={() => void runSignIn(signIn)}
              disabled={submitting}
            >
              Sign in with Google
            </Button>
          </Stack>
          <Typography
            color="text.secondary"
            variant="caption"
            sx={{ textAlign: "center" }}
          >
            Access requires an internal @badmintonwarriors.com account plus
            explicit platform admin approval.
          </Typography>
        </Stack>
      </Paper>
    </Box>
  );
}
